/**
 * Ribbon-aware question-number utilities.
 *
 * Question numbers use a hierarchical "ribbon" format where the hierarchy is
 * encoded in the string itself (e.g. `"9(a)"`, `"5(a)(i)"`). Each ribbon part
 * is stored as an independent document with its own `id` — this module
 * provides the parsing + sorting helpers used to group and order them.
 *
 * @format
 */
import { QuestionSeries } from "./question";

const PART_PATTERN = /\(([^()]*)\)/g;
const ROMAN_NUMERAL_PATTERN = /^[ivxlcdm]+$/i;

const ROMAN_VALUES: Record<string, number> = {
	i: 1,
	v: 5,
	x: 10,
};

/**
 * Parse a ribbon question number into its root + ordered parts.
 *
 * Examples:
 *   - `"1"`       → `{ root: "1",  parts: [] }`
 *   - `"9(a)"`    → `{ root: "9",  parts: ["a"] }`
 *   - `"5(a)(i)"` → `{ root: "5",  parts: ["a", "i"] }`
 *   - `"12(b)(iii)"` → `{ root: "12", parts: ["b", "iii"] }`
 *
 * The root is the portion preceding the first `(`, trimmed. Parts are the
 * contents of each `(...)` group in order. Empty parts are preserved as
 * empty strings so the caller can detect malformed input if desired.
 */
export function parseQuestionNumber(qn: string): { root: string; parts: string[] } {
	if (typeof qn !== "string") {
		return { root: "", parts: [] };
	}
	const trimmed = qn.trim();
	const firstParen = trimmed.indexOf("(");
	const root = (firstParen === -1 ? trimmed : trimmed.slice(0, firstParen)).trim();
	const parts: string[] = [];
	const pattern = new RegExp(PART_PATTERN.source, "g");
	let match: RegExpExecArray | null;
	while ((match = pattern.exec(trimmed)) !== null) {
		parts.push(match[1].trim());
	}
	return { root, parts };
}

/**
 * Return the question number of the immediate parent in the ribbon
 * hierarchy, or `null` if `qn` is a top-level item.
 *
 * Examples:
 *   - `"10"`        → `null`
 *   - `"10(a)"`     → `"10"`
 *   - `"10(c)(i)"`  → `"10(c)"`
 *   - `"5(a)(ii)"`  → `"5(a)"`
 *
 * The output preserves the original root spelling; only the trailing
 * `(...)` group is dropped.
 */
export function getParentQuestionNumber(qn: string): string | null {
	if (typeof qn !== "string") {
		return null;
	}
	const trimmed = qn.trim();
	const lastOpen = trimmed.lastIndexOf("(");
	const lastClose = trimmed.lastIndexOf(")");
	if (lastOpen === -1 || lastClose === -1 || lastClose < lastOpen) {
		return null;
	}
	return trimmed.slice(0, lastOpen).trim();
}

/**
 * Strip ribbon `(...)` groups and return the numeric root prefix.
 *
 * Examples:
 *   - `"1"`         → `"1"`
 *   - `"9(a)"`      → `"9"`
 *   - `"5(a)(i)"`   → `"5"`
 *   - `"10(c)(ii)"` → `"10"`
 *
 * Defensive: throws if the input does not start with one or more digits,
 * since downstream grouping relies on a numeric root.
 */
export function extractRoot(qn: string): string {
	if (typeof qn !== "string") {
		throw new Error(`Question number must be a string, got ${typeof qn}`);
	}
	const trimmed = qn.trim();
	const match = /^(\d+)/.exec(trimmed);
	if (!match) {
		throw new Error(`Question number must start with digits: ${JSON.stringify(qn)}`);
	}
	return match[1];
}

// Seed's buildId
export function buildId(row: {
	brand: string;
	paperCode: string;
	series: QuestionSeries;
	questionNumber: string;
}): string {
	const parts = [row.brand, row.paperCode, `${row.series.season}-${row.series.year}`, row.questionNumber];
	return parts
		.map((p) => String(p).trim())
		.join("--")
		.toLowerCase();
}

/**
 * Build the deterministic parent-group identifier for a question.
 *
 * Mirrors the seed's `buildId` shape but always uses the question-number
 * root, so every item in a parent group resolves to the same id:
 *
 *   `{brand}--{paperCode}--{season-year}--{root}` (lowercased)
 *
 * For a true root question (e.g. `"5"`), `parentQuestionId === id` when
 * the seed's deterministic id scheme is in play.
 */
export function buildParentQuestionId(input: {
	brand: string;
	paperCode: string;
	series: { season: string; year: number };
	rootQuestionNumber: string;
}): string {
	const parts = [input.brand, input.paperCode, `${input.series.season}-${input.series.year}`, input.rootQuestionNumber];
	return parts
		.map((p) => String(p).trim())
		.join("--")
		.toLowerCase();
}

function romanToInt(roman: string): number | null {
	let total = 0;
	let previous = 0;
	for (let i = roman.length - 1; i >= 0; i--) {
		const current = ROMAN_VALUES[roman[i].toLowerCase()];
		if (current === undefined) {
			return null;
		}
		if (current < previous) {
			total -= current;
		} else {
			total += current;
		}
		previous = current;
	}
	return total;
}

/**
 * Compare two ribbon parts (either letters, roman numerals, or numbers).
 *
 * - Pure digits compare numerically.
 * - Roman numerals compare by integer value.
 * - Everything else compares case-insensitively by locale.
 */
function comparePart(a: string, b: string): number {
	if (a === b) {
		return 0;
	}
	const aNum = /^\d+$/.test(a) ? Number(a) : null;
	const bNum = /^\d+$/.test(b) ? Number(b) : null;
	if (aNum !== null && bNum !== null) {
		return aNum - bNum;
	}
	const aRoman = ROMAN_NUMERAL_PATTERN.test(a) ? romanToInt(a) : null;
	const bRoman = ROMAN_NUMERAL_PATTERN.test(b) ? romanToInt(b) : null;
	if (aRoman !== null && bRoman !== null) {
		return aRoman - bRoman;
	}
	return a.toLowerCase().localeCompare(b.toLowerCase());
}

/**
 * Natural-sort comparator for ribbon question numbers.
 *
 * Orders by root first (numerically when both roots are integers), then by
 * each subsequent part. A shorter part list sorts before a longer one with
 * the same prefix (so `"9"` precedes `"9(a)"`, matching the ribbon ordering
 * of `1, 2, ..., 9, 9(a), 9(b), 10`).
 */
export function compareQuestionNumbers(a: string, b: string): number {
	const parsedA = parseQuestionNumber(a);
	const parsedB = parseQuestionNumber(b);
	const rootCompare = comparePart(parsedA.root, parsedB.root);
	if (rootCompare !== 0) {
		return rootCompare;
	}
	const max = Math.max(parsedA.parts.length, parsedB.parts.length);
	for (let i = 0; i < max; i++) {
		const partA = parsedA.parts[i];
		const partB = parsedB.parts[i];
		if (partA === undefined) {
			return -1;
		}
		if (partB === undefined) {
			return 1;
		}
		const diff = comparePart(partA, partB);
		if (diff !== 0) {
			return diff;
		}
	}
	return 0;
}

/**
 * Return the hierarchy depth of a question number. A bare root (e.g. `"9"`)
 * has depth 0. Each parenthesised group adds one level:
 *
 *   - `"9"`        → 0
 *   - `"9(a)"`     → 1
 *   - `"9(a)(i)"`  → 2
 */
export function questionNumberDepth(qn: string): number {
	if (typeof qn !== "string") {
		return 0;
	}
	const matches = qn.match(PART_PATTERN);
	return matches ? matches.length : 0;
}
